<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="modal-overlay">
    <div class="panel password-panel" @click.stop>
      <div class="panel-header">
        <div class="title-wrapper">
          <h3>Change Password</h3>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <img :src="fillIcon" alt="close" class="close-icon" />
        </button>
      </div>
      <div class="panel-body">
        <!-- Current Password -->
        <div class="form-group">
          <label class="form-label" for="currentPassword">
            <span class="required">*</span> Current Password
          </label>
          <div class="input-wrapper" :class="{ error: errors.currentPassword }">
            <input
              id="currentPassword"
              v-model="formData.currentPassword"
              type="password"
              class="form-input"
              placeholder="Enter current password"
            />
            <img v-if="errors.currentPassword" :src="alertIcon" alt="error" class="error-icon" />
          </div>
          <span v-if="errors.currentPassword" class="error-text">{{ errors.currentPassword }}</span>
        </div>

        <!-- New Password -->
        <div class="form-group">
          <label class="form-label" for="newPassword">
            <span class="required">*</span> New Password
          </label>
          <div class="input-wrapper" :class="{ error: errors.newPassword }">
            <input
              id="newPassword"
              v-model="formData.newPassword"
              type="password"
              class="form-input"
              placeholder="Enter new password"
            />
            <img v-if="errors.newPassword" :src="alertIcon" alt="error" class="error-icon" />
          </div>
          <span v-if="errors.newPassword" class="error-text">{{ errors.newPassword }}</span>
        </div>

        <!-- Confirm Password -->
        <div class="form-group">
          <label class="form-label" for="confirmPassword">
            <span class="required">*</span> Confirm Password
          </label>
          <div class="input-wrapper" :class="{ error: errors.confirmPassword }">
            <input
              id="confirmPassword"
              v-model="formData.confirmPassword"
              type="password"
              class="form-input"
              placeholder="Enter confirm password"
            />
            <img v-if="errors.confirmPassword" :src="alertIcon" alt="error" class="error-icon" />
          </div>
          <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
        </div>

        <!-- 底部按钮区域 -->
        <div class="form-actions right-align">
          <button class="btn btn-cancel" @click="handleReset">Cancel</button>
          <button class="btn btn-submit" :disabled="saving" @click="handleChange">Submit</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { reactive, inject } from 'vue'
import { changePasswordApi } from '@/api/admin'
import fillIcon from '@/assets/Fill 1@2x.png'
import alertIcon from '@/assets/alert.png'
import { usePopupAction } from '@/composables/usePopupAction'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', result: 'success' | 'warning' | 'error'): void
}>()

const formData = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const errors = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const { pending: saving, run: runChange } = usePopupAction()

// 使用全局的 showToast 方法
const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast')!

function handleReset() {
  formData.currentPassword = ''
  formData.newPassword = ''
  formData.confirmPassword = ''
  errors.currentPassword = ''
  errors.newPassword = ''
  errors.confirmPassword = ''
  emit('close')
}

async function handleChange() {
  // 清除所有错误提示
  errors.currentPassword = ''
  errors.newPassword = ''
  errors.confirmPassword = ''

  // 验证当前密码
  if (!formData.currentPassword) {
    errors.currentPassword = 'Current password is required'
    return
  }
  if (formData.currentPassword.length < 8) {
    errors.currentPassword = 'Current Password is invalid'
    showToast('error', 'Current Password is invalid')
    return
  }

  // 验证新密码
  if (!formData.newPassword) {
    errors.newPassword = 'New password is required'
    return
  }
  const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/
  if (!passwordRegex.test(formData.newPassword)) {
    errors.newPassword = 'At least 8 characters'
    showToast(
      'warning',
      'At least 8 characters, including 1 uppercase letter and 1 special character.',
    )
    return
  }

  // 验证确认密码
  if (!formData.confirmPassword) {
    errors.confirmPassword = 'Please confirm password'
    return
  }
  if (formData.newPassword !== formData.confirmPassword) {
    errors.confirmPassword = 'Password do not match'
    showToast('error', 'Password do not match')
    return
  }

  // 全部验证通过，调用后端 API
  if (saving.value) return
  await runChange(async () => {
    try {
      const payload = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      }
      console.log('Change password payload:', payload)
      const res = await changePasswordApi(payload)
      console.log('Change password response:', res)
      if (res?.code === 200 || res?.code === 0) {
        showToast('success', 'Successfully changed')
        handleReset()
        logoutAndRedirect()
      } else {
        const msg = res?.msg?.trim() || 'Current Password is invalid'
        errors.currentPassword = msg
        showToast('error', msg)
      }
    } catch (error: unknown) {
      console.error('Change password error:', error)
      const isAuthError =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 401
      if (isAuthError) {
        showToast('success', 'Successfully changed')
        handleReset()
        logoutAndRedirect()
        return
      }
      const msg = 'Current Password is invalid'
      errors.currentPassword = msg
      showToast('error', msg)
    }
  })
}

function logoutAndRedirect() {
  localStorage.removeItem('token')
  setTimeout(() => {
    window.location.href = '/?passwordChanged=1'
  }, 600)
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 0;
}
/* 面板基础样式 */
.panel {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 530px;
  height: auto;
  max-height: 90vh;
  overflow-y: auto;
  max-width: none;
  box-sizing: border-box;
}

.password-panel {
  min-height: 376px;
  display: flex;
  flex-direction: column;
}

.password-panel .panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.password-panel .form-actions {
  margin-top: auto;
  flex-shrink: 0;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px 24px;
  border-bottom: none;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #000000;
  white-space: nowrap;
  line-height: 24px;
}

.title-wrapper {
  display: flex;
  align-items: center;
}

/* 面板内容区 */
.panel-body {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
}
/* 关闭按钮 */
.close-btn {
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

/* 表单组 */
.form-group {
  margin-top: 0;
  margin-bottom: 16px;
}

.form-group:first-child {
  margin-top: 0;
}

.form-group:last-child {
  margin-bottom: 0;
}

/* 标签样式 */
.form-label {
  display: block;
  margin-top: 0;
  margin-bottom: 16px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #000000;
}
.required {
  color: #ff4d4f;
  margin-right: 4px;
}

/* 输入框容器 */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 482px;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 16px;
  background: #fffafa;
}

/* 输入框样式 */
.form-input {
  width: 100%;
  height: 32px;
  padding: 6px 16px;
  border: 1px solid #E0E0E0;
  border-radius: 16px;
  background: #fff;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #000000;
  caret-color: #000000;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.input-wrapper.error .form-input {
  border: none;
  background: transparent;
}

.form-input:focus {
  border-color: #666666;
  box-shadow: none;
}

.error-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

/* 错误提示文字 */
.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 24px;
  padding-bottom: 24px;
  margin: 0;
}

/* 按钮基础样式 */
.btn {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Cancel 按钮（与 Profile 一致） */
.btn-cancel {
  border: 1px solid #000000;
  background: #fff;
  color: #000000;
}

.btn-cancel:hover {
  background: #000000;
  color: #ffffff;
}

/* Submit 按钮（与 Profile 一致） */
.btn-submit {
  border: 1px solid #000;
  background: #000;
  color: #fff;
}

.btn-submit:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .panel {
    width: 95%;
    max-width: 500px;
  }

  .panel-body {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 10px;
  }

  .panel {
    width: 100%;
    max-width: 100%;
    border-radius: 8px;
  }
}
</style>
